/**
 * The request of the price analyzers that carry the Cars selector — DOR, LOR, Rank Analyzer (CU-869fmpqfm, UX Obs. 1
 * and 5). Only the latest request paints (the gate Season uses). Two ways to ask:
 *
 * - `keep: true` (a car picked, Min / Max): the table on screen stays under "Updating…" and nothing is reset, so the
 *   Excel, the Evolution and the decision marks keep describing the table the user sees until the answer (C1);
 * - `keep: false` (Search): as before — `resetSignals(clearTab)`, the skeleton.
 *
 * An error with a table on screen becomes `analyzerError` (a note in the card with "Try again", which repeats the last
 * request); with nothing on screen it is the toast of before, or "not found" on a 404.
 *
 * Pure and alias-free: the signals, the post and the reset are passed in. `node --test` runs it.
 */
import { createLatestGate, shouldPaint } from '../../../@components/KindPillsField/latestOnly.js'

const hasAnswer = data => !!data && Object.keys(data).length > 0

export function createAnalyzerRequest({ state, post, resetSignals, onError, batch = fn => fn() }) {
	const gate = createLatestGate()

	function run(request) {
		const { payload, type, keep = false, clearTab = false, after = null, extra = null } = request
		const fail = request.onError || onError
		const id = gate.next()
		const keepNow = keep && hasAnswer(state.responseData.value)

		batch(() => {
			state.analyzerError.value = null
			if (keepNow) {
				state.analyzerUpdating.value = true
				return
			}
			state.analyzerUpdating.value = false
			state.isAnalyzerLoading.value = true
			state.responseData.value = {}
			resetSignals(clearTab)
		})

		return post(payload).then(res => {
			if (!shouldPaint(gate, id)) return
			batch(() => {
				state.responseData.value = { ...res, type, ...extra }
				state.analyzerFilters.value = res?.filters
				state.isAnalyzerLoading.value = false
				state.analyzerUpdating.value = false
			})
			after?.(res)
		}).catch(err => {
			if (!shouldPaint(gate, id, err)) return
			batch(() => {
				state.isAnalyzerLoading.value = false
				state.analyzerUpdating.value = false
			})
			// what is open is not this account's (or is gone): say so instead of the generic toast
			if (err?.status === 404) {
				state.analyzerNotFound.value = true
				return
			}
			if (keepNow) {
				state.analyzerError.value = { status: err?.status ?? null, retry: () => run({ ...request, keep: true }) }
				return
			}
			fail(err)
		})
	}

	return { run }
}
